import { FC, useCallback, useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Button from '@components/Button/ui/Button';
import mammoth from 'mammoth/mammoth.browser';

interface TermsPrivacyModalProps {
  open: boolean;
  onAccept: () => void;
  onClose: () => void;
  termsDocxUrl: string;
  privacyDocxUrl: string;
  title?: string;
}

const TermsPrivacyModal: FC<TermsPrivacyModalProps> = ({
  open,
  onAccept,
  onClose,
  termsDocxUrl,
  privacyDocxUrl,
  title = 'Terms of Service and Privacy Policy',
}) => {
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [termsHtml, setTermsHtml] = useState<string>('');
  const [termsLoading, setTermsLoading] = useState<boolean>(false);
  const [termsError, setTermsError] = useState<string | null>(null);

  const [privacyHtml, setPrivacyHtml] = useState<string>('');
  const [privacyLoading, setPrivacyLoading] = useState<boolean>(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);

  const [acceptEnabled, setAcceptEnabled] = useState<boolean>(false);

  const fetchDocxToHtml = useCallback(async (url: string) => {
    const res = await fetch(encodeURI(url));
    if (!res.ok) throw new Error(`Failed to load ${url}`);
    const buffer = await res.arrayBuffer();
    const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
    return result.value || '';
  }, []);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    setAcceptEnabled(false);

    const load = async () => {
      try {
        setTermsLoading(true);
        setTermsError(null);
        const html = await fetchDocxToHtml(termsDocxUrl);
        if (isMounted) setTermsHtml(html);
      } catch (e: any) {
        if (isMounted)
          setTermsError(e?.message || 'Unable to load Terms document.');
      } finally {
        if (isMounted) setTermsLoading(false);
      }

      try {
        setPrivacyLoading(true);
        setPrivacyError(null);
        const html = await fetchDocxToHtml(privacyDocxUrl);
        if (isMounted) setPrivacyHtml(html);
      } catch (e: any) {
        if (isMounted)
          setPrivacyError(e?.message || 'Unable to load Privacy document.');
      } finally {
        if (isMounted) setPrivacyLoading(false);
      }

      // after content potentially renders, evaluate scrollability next tick
      setTimeout(() => {
        const el = contentRef.current;
        if (!el) return;
        const canAcceptNow = el.scrollHeight <= el.clientHeight + 2; // small tolerance
        setAcceptEnabled(canAcceptNow);
      }, 0);
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [open, fetchDocxToHtml, termsDocxUrl, privacyDocxUrl]);

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const isBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2; // tolerance
    if (isBottom) setAcceptEnabled(true);
  }, []);

  const loadingAny = termsLoading || privacyLoading;

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        if (reason === 'escapeKeyDown') return;
      }}
      disableEscapeKeyDown
      fullWidth
      maxWidth="xl"
      scroll="paper"
      PaperProps={{
        sx: {
          width: { xs: '100%', md: '90vw' },
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{title}</Typography>
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ maxHeight: '80vh' }}
        ref={contentRef}
        onScroll={handleScroll}
      >
        {loadingAny && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!loadingAny && (
          <>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please read our Terms of Service and Privacy Policy before
              continuing.
            </Typography>
            {termsError ? (
              <Typography variant="body2" color="error" paragraph>
                {termsError}
              </Typography>
            ) : (
              <div
                style={{ marginBottom: 24 }}
                dangerouslySetInnerHTML={{ __html: termsHtml }}
              />
            )}

            {privacyError ? (
              <Typography variant="body2" color="error" paragraph>
                {privacyError}
              </Typography>
            ) : (
              <div dangerouslySetInnerHTML={{ __html: privacyHtml }} />
            )}

            {!acceptEnabled && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={2}
              >
                Scroll to the bottom to enable the Accept button.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button variant="active" onClick={onAccept} disabled={!acceptEnabled}>
          I Accept Terms and Privacy Policy
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsPrivacyModal;
